
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Compass,
    Palette,
    Code2,
    Link2,
    TrendingUp,
} from 'lucide-react';

const steps = [
    {
        step: "01",
        name: "Discover",
        icon: Compass,
        desc: "Understand your business processes, existing platform and transformation objectives.",
        image: "/assets/services/bg3.png",
    },
    {
        step: "02",
        name: "Design",
        icon: Palette,
        desc: "Define the architecture, workflows, user experience and implementation roadmap.",
        image: "/assets/services/bg3.png",
    },
    {
        step: "03",
        name: "Implement",
        icon: Code2,
        desc: "Configure, customize and develop the ServiceNow platform around your requirements.",
        image: "/assets/services/bg3.png",
    },
    {
        step: "04",
        name: "Integrate",
        icon: Link2,
        desc: "Connect ServiceNow with your existing enterprise applications and technology ecosystem.",
        image: "/assets/services/bg3.png",
    },
    {
        step: "05",
        name: "Optimize",
        icon: TrendingUp,
        desc: "Continuously monitor, improve and automate your ServiceNow environment to maximize business value.",
        image: "/assets/services/bg3.png",
    },
];

function ServiceNowApproachSection() {
    const [activeStep, setActiveStep] = useState(0);

    return (
        <section className="relative w-full overflow-hidden bg-white pb-[60px] lg:pb-[90px] mt-[80px]">
            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-[20px] lg:px-[80px]">

                {/* ================= HEADER ================= */}
                <div className="mb-[36px] lg:mb-[48px] flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div className="max-w-[700px]">
                        <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
                            From discovery to production
                        </p>
                        <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] leading-[1.2] text-[#01386A] tracking-tight">
                            Our ServiceNow <br className="hidden sm:block" />
                            Transformation Approach
                        </h2>
                    </div>

                    <div className="max-w-[480px]">
                        <p className="font-graphik font-[500] text-[14px] sm:text-[16px] text-[#49577A] leading-[1.6]">
                            A structured approach that takes your ServiceNow journey from initial discovery through continuous optimization and measurable business outcomes.
                        </p>
                    </div>
                </div>

                {/* ================= ACCORDION STRIP ================= */}
                <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch min-h-[480px] lg:h-[480px]">
                    {steps.map((item, index) => {
                        const isActive = activeStep === index;
                        const Icon = item.icon;

                        return (
                            <motion.div
                                key={item.step}
                                onClick={() => setActiveStep(index)}
                                onMouseEnter={() => setActiveStep(index)}
                                layout
                                transition={{ duration: 0.45, ease: [0.25, 1, 0.5, 1] }}
                                className={`relative cursor-pointer overflow-hidden transition-all duration-300 ${isActive
                                    ? "flex-1 lg:flex-[3.5] bg-[#01386A] text-white shadow-xl"
                                    : "flex-none lg:flex-1 bg-white border border-[#DCE3E9] hover:border-[#01386A]/40"
                                    }`}
                            >
                                {isActive ? (
                                    /* ACTIVE EXPANDED CARD */
                                    <div className="flex flex-col h-full justify-between">
                                        {/* Top Image Banner with Floating Icon */}
                                        <div className="relative w-full h-[220px] sm:h-[240px] overflow-hidden bg-[#0a1630]">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover opacity-90 transition-transform duration-700 hover:scale-105"
                                            />
                                            {/* <div className="absolute top-5 left-6 w-11 h-11 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white">
                                                <Icon size={20} />
                                            </div> */}
                                        </div>

                                        {/* Content Box */}
                                        <div className="p-6 sm:p-8 flex flex-col justify-between flex-1 relative">
                                            {/* Watermark Step Number */}
                                            <span className="absolute top-6 right-8 font-sora text-[20px] sm:text-[30px] font-[600] text-white/25 select-none pointer-events-none">
                                                {item.step}
                                            </span>

                                            <div>
                                                <h3 className="font-sora text-[20px] sm:text-[30px] font-[600] text-white mb-4">
                                                    {item.name}
                                                </h3>
                                                <p className="font-graphik text-[14px] lg:text-[16px] font-[500] leading-[1.65] text-white/85 max-w-[540px]">
                                                    {item.desc}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* INACTIVE COLLAPSED PILLAR */
                                    <div className="flex lg:flex-col items-center justify-between h-full p-5 sm:p-6 lg:py-8 lg:px-4">
                                        {/* Step Number */}
                                        <span className="font-sora text-[20px] sm:text-[30px] font-[600] text-[#49577A]/70">
                                            {item.step}
                                        </span>

                                        {/* Center Vertical Divider Line */}
                                        <div className="hidden lg:block w-[1px] h-[140px] bg-[#DCE3E9]" />

                                        {/* Bottom Vertical Text Label */}
                                        <div className="lg:[writing-mode:vertical-rl] lg:rotate-180 flex items-center justify-center">
                                            <span className="font-sora text-[16px] sm:text-[20px] font-[600] text-[#01386A] tracking-wide whitespace-nowrap">
                                                {item.name}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}

export default ServiceNowApproachSection;